import { type FC } from "react";

import { OverviewHeader } from "../../../../../components/sectionHeader";
import { DeletePdfButton } from "./DeletePdfButton";
import { CreatePdfButton } from "./CreatePdfButton";
import { RolePermissions } from "../../../../../enums";
import { AddToPacksButton } from "../../../../../components/buttons/AddToPacksButton/AddToPacksButton";

import Restricted from "../../../../Application/Restricted";
import AddAssetsToPackModal from "../../../../../components/modal/AddAssetsToPacksModal/AddAssetsToPackModal";
import TagsManageContainer from "../containers/TagsManageContainer";
import styles from "./pdfOverviewHeader.module.scss";
import useToggle from "../../../../../hooks/useToggle";
import { ToggleVisibilityButton } from "components/buttons/linkButtons/toggleVisibilityButton/ToggleVisibilityButton";

export interface PdfOverviewHeaderProps {
  selectedIds: number[];
  pdfDeletionHandler: (selectedIds: number[], flowsCount?: number, packsCount?: number) => void;
  purchasedSelected: boolean;
  undeletableSelected: boolean;
  draftSelected: boolean;
  hiddenSelected: boolean;
  visibleSelected: boolean;
  unpublishedSelected: boolean;
  clearSelection: () => void;
  onToggleVisibility: (selectedIds: number[], hidden: boolean) => () => void;
}

const addToPacksPermissions = [RolePermissions.PacksManage, RolePermissions.AssetsCreate];

export const PdfOverviewHeader: FC<PdfOverviewHeaderProps> = (props) => {
  const {
    purchasedSelected,
    undeletableSelected,
    draftSelected,
    pdfDeletionHandler,
    selectedIds,
    clearSelection,
    onToggleVisibility,
    hiddenSelected,
    visibleSelected,
    unpublishedSelected,
  } = props;

  const [packsModalShown, togglePackModal] = useToggle();

  const visibility = !(visibleSelected && hiddenSelected);

  const handleModalClose = () => {
    togglePackModal();
    clearSelection();
  };

  const renderBatchButtons = () => {
    const hiddenAndShownSelected = hiddenSelected && visibleSelected;

    return (
      <>
        {visibility && (
          <Restricted permissions={[RolePermissions.AssetsCreate]}>
            <ToggleVisibilityButton
              onClick={onToggleVisibility(selectedIds, hiddenSelected)}
              hidden={hiddenSelected}
              isDisabled={hiddenAndShownSelected || unpublishedSelected}
            />
          </Restricted>
        )}
        <Restricted permissions={addToPacksPermissions}>
          <AddToPacksButton onClick={togglePackModal} isDisabled={purchasedSelected || draftSelected} />
        </Restricted>
        <DeletePdfButton
          purchasedSelected={purchasedSelected}
          undeletableSelected={undeletableSelected}
          onClick={() => pdfDeletionHandler(selectedIds)}
        />
        <TagsManageContainer
          entity="PDFs"
          isDisabled={purchasedSelected}
          selectedIds={selectedIds}
          onComplete={clearSelection}
          onClose={clearSelection}
        />
        <AddAssetsToPackModal
          showModal={packsModalShown}
          onClose={handleModalClose}
          onComplete={props.clearSelection}
          selectedItemIds={selectedIds}
          contentType="Pdf"
        />
      </>
    );
  };

  return (
    <OverviewHeader title="PDFs" itemName="PDF" titleForGA="Assets - PDFs" selectedItemsAmount={selectedIds.length}>
      <div className={styles.root}>{selectedIds.length ? renderBatchButtons() : <CreatePdfButton />}</div>
    </OverviewHeader>
  );
};
